// Pure step/field configuration for the Ontario Progress Report Card - Grades 7-8.
// No rendering logic lives here. Page.jsx imports everything it needs from this file.

import { twoPlans, threePlans, fourPlans, sixPlans } from './options.js';

// ── Step definitions ──────────────────────────────────────────────────────────

export const STEPS = [
  { id: 1, title: 'Student Info' },
  { id: 2, title: 'School Info' },
  { id: 3, title: 'Attendance' },
  { id: 4, title: 'Learning Skills' },
  { id: 5, title: 'Subject Grades' },
  { id: 6, title: 'Arts & Comments' },
];

// ── Which schema keys belong to each step ─────────────────────────────────────
// Used for step-completion detection (all fields non-empty → step complete).

export const STEP_FIELDS = {
  1: ['studentName', 'oen', 'date', 'grade', 'teacher'],
  2: ['board', 'school', 'address1', 'address2', 'principal', 'telephone'],
  3: ['daysAbsent', 'totalDaysAbsent', 'timesLate', 'totalTimesLate', 'boardInformation1'],
  4: [
    'responsibility', 'organization', 'independentWork',
    'collaboration', 'initiative', 'selfRegulation',
    'strengthsNextStepsLSWH',
  ],
  5: [
    'languagePlan', 'languageResult',
    'frenchPlan', 'frenchResult',
    'nativeLanguage', 'nativeLanguagePlan', 'nativeLanguageResult',
    'mathematicsPlan', 'mathematicsResult',
    'scienceAndTechnologyPlan', 'scienceAndTechnologyResult',
    'historyPlan', 'historyResult',
    'geographyPlan', 'geographyResult',
    'healthEducationPlan', 'healthEducationResult',
    'physicalEducationPlan', 'physicalEducationResult',
  ],
  6: [
    'artsHeader',
    'arts1', 'arts1Plan', 'arts1Result',
    'arts2', 'arts2Plan', 'arts2Result',
    'arts3', 'arts3Plan', 'arts3Result',
    'arts4', 'arts4Plan', 'arts4Result',
    'optionalStudies', 'optionalStudiesPlan', 'optionalStudiesResult',
    'strengthsNextStepsSubjects',
    'boardInformation2',
  ],
};

// ── Human-readable label for each schema key ──────────────────────────────────
// Used in review/diff views and accessibility labels.

export const FIELD_LABELS = {
  date:        'Date',
  studentName: 'Student Name',
  oen:         'OEN',
  grade:       'Grade',
  teacher:     'Teacher',

  board:     'Board',
  school:    'School',
  address1:  'Address Line 1',
  address2:  'Address Line 2',
  principal: 'Principal',
  telephone: 'Telephone',

  daysAbsent:        'Days Absent (this term)',
  totalDaysAbsent:   'Total Days Absent',
  timesLate:         'Times Late (this term)',
  totalTimesLate:    'Total Times Late',
  boardInformation1: 'Board Information (Page 1)',

  responsibility:         'Responsibility',
  organization:           'Organization',
  independentWork:        'Independent Work',
  collaboration:          'Collaboration',
  initiative:             'Initiative',
  selfRegulation:         'Self-Regulation',
  strengthsNextStepsLSWH: 'Strengths & Next Steps - Learning Skills',

  languagePlan:    'Language Plan',
  languageResult:  'Language Result',
  frenchPlan:      'French Plan',
  frenchResult:    'French Result',
  nativeLanguage:      'Native Language',
  nativeLanguagePlan:  'Native Language Plan',
  nativeLanguageResult:'Native Language Result',
  mathematicsPlan:   'Mathematics Plan',
  mathematicsResult: 'Mathematics Result',
  scienceAndTechnologyPlan:   'Science & Technology Plan',
  scienceAndTechnologyResult: 'Science & Technology Result',
  historyPlan:   'History Plan',
  historyResult: 'History Result',
  geographyPlan:   'Geography Plan',
  geographyResult: 'Geography Result',
  healthEducationPlan:   'Health Education Plan',
  healthEducationResult: 'Health Education Result',
  physicalEducationPlan:   'Physical Education Plan',
  physicalEducationResult: 'Physical Education Result',

  artsHeader: 'Arts Header',
  arts1:       'Arts Subject 1',
  arts1Plan:   'Arts Subject 1 Plan',
  arts1Result: 'Arts Subject 1 Result',
  arts2:       'Arts Subject 2',
  arts2Plan:   'Arts Subject 2 Plan',
  arts2Result: 'Arts Subject 2 Result',
  arts3:       'Arts Subject 3',
  arts3Plan:   'Arts Subject 3 Plan',
  arts3Result: 'Arts Subject 3 Result',
  arts4:       'Arts Subject 4',
  arts4Plan:   'Arts Subject 4 Plan',
  arts4Result: 'Arts Subject 4 Result',
  optionalStudies:       'Optional Studies',
  optionalStudiesPlan:   'Optional Studies Plan',
  optionalStudiesResult: 'Optional Studies Result',
  strengthsNextStepsSubjects: 'Strengths & Next Steps - Subjects',
  boardInformation2: 'Board Information (Page 2)',
};

// ── Core subject row configs for Step 5 ───────────────────────────────────────
// Gr 7-8 replaces Social Studies with History + Geography.
// History and Geography both use fourPlans (ESLELD / IEP / French / N/A).

export const CORE_SUBJECTS = [
  { label: 'Language',             planField: 'languagePlan',            resultField: 'languageResult',            plans: twoPlans },
  { label: 'French',               planField: 'frenchPlan',              resultField: 'frenchResult',              plans: sixPlans },
  { label: 'Native Language',      planField: 'nativeLanguagePlan',      resultField: 'nativeLanguageResult',      plans: twoPlans,    nameField: 'nativeLanguage' },
  { label: 'Mathematics',          planField: 'mathematicsPlan',         resultField: 'mathematicsResult',         plans: threePlans },
  { label: 'Science & Technology', planField: 'scienceAndTechnologyPlan',resultField: 'scienceAndTechnologyResult',plans: threePlans },
  { label: 'History',              planField: 'historyPlan',             resultField: 'historyResult',             plans: fourPlans },
  { label: 'Geography',            planField: 'geographyPlan',           resultField: 'geographyResult',           plans: fourPlans },
  { label: 'Health Education',     planField: 'healthEducationPlan',     resultField: 'healthEducationResult',     plans: threePlans },
  { label: 'Physical Education',   planField: 'physicalEducationPlan',   resultField: 'physicalEducationResult',   plans: threePlans },
];

// ── Arts row configs for Step 6 ───────────────────────────────────────────────

export const ARTS_ROWS = [
  { nameField: 'arts1', planField: 'arts1Plan', resultField: 'arts1Result', placeholder: 'e.g. Visual Arts' },
  { nameField: 'arts2', planField: 'arts2Plan', resultField: 'arts2Result', placeholder: 'e.g. Music' },
  { nameField: 'arts3', planField: 'arts3Plan', resultField: 'arts3Result', placeholder: 'e.g. Drama' },
  { nameField: 'arts4', planField: 'arts4Plan', resultField: 'arts4Result', placeholder: 'e.g. Dance' },
];
